/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getEventId } from "Router/RoutesUtils";
import sleep from "utils/sleep";

import { FetchError, FetchSuccess, fetchV2, toJSON } from "..";

// Refresh runs on every page load and a single failure was logging users out
// (notably Safari/WebKit, which rejects transient fetches with "TypeError: Load
// failed" far more aggressively than Chromium). Retry up to 3 times silently —
// no toast or Sentry noise — and only surface the error if every attempt fails.
const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_RETRY_BASE_MS = 400;

export const persistLoginSession = async () => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const eventId = getEventId();
  if (!token || token.length === 0) throw new FetchError();

  let lastError;
  for (let attempt = 1; attempt <= REFRESH_MAX_ATTEMPTS; attempt++) {
    try {
      const request = await fetchV2(
        `refresh?role=admin&event_id=${eventId}`,
        {
          method: "POST",
        },
        {
          page: "login",
          shouldSearchForPage: false,
        },
      );

      const user = await toJSON(request);
      if (user.token) {
        localStorage.setItem("token", user.token);
      }

      if (user.setting_id) {
        localStorage.setItem("setting_id", user.setting_id);
      }

      return new FetchSuccess(request, {
        shapeData: (data) => ({
          user: data,
          role,
        }),
      });
    } catch (res) {
      // Retry silently: swallow the error (no FetchError → no toast/Sentry)
      // and back off before the next attempt.
      lastError = res;

      if (attempt < REFRESH_MAX_ATTEMPTS) {
        await sleep(REFRESH_RETRY_BASE_MS * attempt);
      }
    }
  }

  // Every attempt failed — now surface it, preserving the original behavior.
  if (lastError?.status === 401) {
    localStorage.clear();
  }

  throw new FetchError(lastError);
};

export const logout = async () => {
  try {
    const res = await fetchV2(
      `logout-admin`,
      {
        method: "POST",
      },
      {
        page: "/",
        shouldSearchForPage: false,
      },
    );

    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res);
  }
};
